import { DatumReader, DataSourceOptions, TypedDatumDefinition, Datum, TemporalCoordinate, SpatialCoordinate } from './types';
import { DataList } from './data-list';
import * as util from './util';
import { Visualizer, VisualizerControl, VisualizationUpdateLevel, VISUALIZER_KEY_MAP } from './visualizer';
import { Application } from '../application/application';
import { Timeline } from '../timeline/timeline';
import { TimelineRange, CursorMillis } from '../timeline/timeline-common';
import { Map as TLMDMap } from '../map/map';
import { Observable, Subscription } from 'rxjs';


export class DataSource implements DatumReader {
	private timeline: Timeline;
	private map: TLMDMap;

	private types: string[];
	private typeVisualisationControls: { [key: string]: VisualizerControl };
	private typeDefinitions: { [key: string]: TypedDatumDefinition };
	private typeDataMap: { [key: string]: DataList };

//	private requestStack: RequestStackItem[];

	private timelineSubscriptions: Subscription[];
	private mapSubscriptions: Subscription[];

	private metaData: { timeRange: TimelineRange, animationTiming: CursorMillis[], version: number }

	private visualizers: { [key: string]: Visualizer };
	
	private metaObservable: Observable<{ timeRange: TimelineRange, animationTiming: CursorMillis[], version: number }>;
	private notifyNewMeta: () => void

	public constructor(private options: DataSourceOptions, private application: Application) {
		this.timelineSubscriptions = [];
		this.mapSubscriptions = [];
		this.metaData = { timeRange: null, animationTiming: null, version: null };
//		this.requestStack = [];

		this.types = [];
		this.typeDefinitions = {};
		this.typeDataMap = {};
		this.typeVisualisationControls = {};
		this.visualizers = {};
		this.notifyNewMeta = () => {};
		let self = this;
		
		this.metaObservable = new Observable<{ timeRange: TimelineRange, animationTiming: CursorMillis[], version: number }>(subscriber => {
			self.notifyNewMeta = () => {
				try {
					subscriber.next(self.getMeta());
				} catch (err) {
					subscriber.error(err); // delivers an error if it caught one
				}
			}
		});


		this.setTimeline(application.getMainTimeline());
		this.setMap(application.getMainMap());
	}

	public getName(): string {
		return this.options.name;
	}
	public getApplication(): Application {
		return this.application;
	}
	protected getOptions(): DataSourceOptions {
		return this.options;
	}
	public getMeta(): { timeRange: TimelineRange, animationTiming: CursorMillis[], version: number } {
		return this.metaData;
	}

	protected setVisualizerControlForType(type: string, controls: VisualizerControl): string {
			this.typeVisualisationControls[type] = controls;
			return type;
   }		
	public addType(type: TypedDatumDefinition, controls: VisualizerControl): string {
		if (this.types.indexOf(type.id) == -1) {
			this.types.push(type.id);
			this.typeDefinitions[type.id] = type;
			this.typeVisualisationControls[type.id] = controls;
			this.typeDataMap[type.id] = this.createDataList(type.id, this);
			this.typeAdded(type.id);
		} else {
			//type already present
		}
		return type.id;
	}

	public removeType(typeId: string): void {
		let index = this.types.indexOf(typeId);
		if (index != -1) {
			this.types.splice(index, 1);
			this.typeDefinitions[typeId] = undefined;
			delete this.typeDefinitions[typeId];
			this.typeVisualisationControls[typeId] = undefined;
			delete this.typeVisualisationControls[typeId];
			this.typeDataMap[typeId] = undefined;
			delete this.typeDataMap[typeId];
		} else {
			//type not present
		}
	}
	
	protected typeAdded(typeId: string): void{
		console.log(`Source.typeAdded(${typeId})`);
	}


	protected getTypes(): string[] {
		return this.types;
	}

	protected createDataList(type:string, versionProvider: DatumReader, data?: Datum[]): DataList {
		return new DataList(type, versionProvider, data);
	}

	public getDefinitions(): { [key: string]: TypedDatumDefinition } {
		return this.typeDefinitions;
	}

	protected getDataList(type: string) {
		return this.typeDataMap[type];
	}

	/**
	 * Called by application when main timeline becomes available. Propagates to the
	 * viewers
	 */
	public setTimeline(timeline: Timeline) {
		if (this.timeline != null) {
			this.timelineSubscriptions.forEach(subscription => subscription.unsubscribe());
			this.timelineSubscriptions.length = 0;
		}

		this.timeline = timeline;

		if (this.timeline != null) {
			this.timelineSubscriptions.push(...this.subscribeToTimeline());
		}

		Object.values(this.visualizers).forEach(visualizer => visualizer.setTimeline(this.timeline));

//		if (this.options.notTemporal !== true) {
//			this.requestData();
//		}
	}

	public getTimeline(): Timeline {
		return this.timeline;
	}

	protected subscribeToTimeline(): Subscription[] {
		return [
			this.getTimeline().getObservable('mapp-tl-cur').subscribe(_cursorPosition => {
				if (this.options.notTemporal !== true) {
					this.updateVisualizations(VisualizationUpdateLevel.animation);
				}
			})
		];
	}

	/**
	 * Called by application when main map becomes available. Propagates to the
	 * viewers
	 */
	public setMap(map: TLMDMap) {
		if (this.map != null) {
			this.mapSubscriptions.forEach(subscription => subscription.unsubscribe());
			this.mapSubscriptions.length = 0;
		}

		this.map = map;

		if (this.map != null) {
			this.mapSubscriptions.push(...this.subscribeToMap());
		}

		Object.values(this.visualizers).forEach(visualizer => visualizer.setMap(this.map));

		this.updateVisualizations(VisualizationUpdateLevel.visualization, VISUALIZER_KEY_MAP, true);
	}
	
	protected subscribeToMap(): Subscription[] {
		return [
			this.getMap().getObservable('mapp-mp-rng').subscribe(_mapBounds => {
				this.updateVisualizations(VisualizationUpdateLevel.visualization, VISUALIZER_KEY_MAP, true);
			})
		];
	}

	public getMap(): TLMDMap {
		return this.map;
	}
	public addVisualizer(visualizer: Visualizer): Visualizer {
		this.visualizers[visualizer.key()] = visualizer;

		visualizer.setControls(this.typeVisualisationControls);
		visualizer.setTimeline(this.timeline);
		visualizer.setMap(this.map);

		return visualizer;
	}

	public removeVisualizer(visualizerKey: string): Visualizer {
		let visualizer = this.visualizers[visualizerKey];
		if (visualizer) {
			visualizer.setControls(null);
			visualizer.setTimeline(null);
			visualizer.setMap(null);
			this.visualizers[visualizer.key()] = undefined;
			delete this.visualizers[visualizer.key()];
		}
		return visualizer;
	}

	public getVisualizer(visualizerKey: string): Visualizer {
		return this.visualizers[visualizerKey];
	}

	public getVisualizorKeys(): string[] {
		return Object.keys(this.visualizers);
	}

	public authenticate(signInUrl?: string, credentials?: string): void {
		// gives web workers a change to access secured resources with XHR
		signInUrl = signInUrl ? signInUrl : `/${encodeURI(this.application.getDomain())}`;
		credentials = credentials ? credentials : this.application.getDigester().getCredentials();
		//see scalar-field worker/manager on how to actually authorize a web worker
	}

	public onBlur(type: string): void {
		this.forEachDataList((dataList) => {
			//apply filter is called internally
			dataList.setSearchTerm(undefined);
		}, type);

		this.updateSelections(VisualizationUpdateLevel.filter, type);
	}

	public onSearchTerm(type: string, searchTerm: string): void {
		this.forEachDataList((dataList) => {
			//apply filter is called internally
			dataList.setSearchTerm(searchTerm);
		}, type);

		this.updateSelections(VisualizationUpdateLevel.filter, type);
	}
	
	public onDeletionFilterValue(type: string, deletionFilterValue: boolean): void {
		this.forEachDataList((dataList) => {
			//apply filter is called internally
			dataList.setDeletionFilterValue(deletionFilterValue);
		}, type);

		this.updateSelections(VisualizationUpdateLevel.filter, type);
	}

	public onSelection(type: string, selectedDataIds: string | string[]): void {
		let data = this.typeDataMap[type];
		if (data) {
			data.setSelection(selectedDataIds);
		}
		this.updateSelections(VisualizationUpdateLevel.selection, type);
	}


	protected updateSelections(level: VisualizationUpdateLevel, types?: string | string[]): void {
		this.forEachVisualizer((visualizer) => {
			visualizer.updateSelection();
		}, types)

		this.updateVisualizations(level, types);
	}

	protected updateVisualizations(level: VisualizationUpdateLevel, types?: string | string[], filterOnKeys?: boolean): void {
		this.forEachVisualizer((visualizer) => {
			visualizer.refresh(level);
		}, types, filterOnKeys);
	}

	public getData(types?: string[]): { [key: string]: DataList } {
		if (types) {

			return Object.entries(this.typeDataMap)
				.filter(entry => types.includes(entry[0]))
				.reduce((filteredDataMap, entry) => {
					filteredDataMap[entry[0]] = entry[1];
					return filteredDataMap;
				}, <{ [key: string]: DataList }>{});

		} else {

			return this.typeDataMap;

		}
	}

	/**
	 * Implementation of the DatumReader interface. Override when the default
	 * implementation is incorrrect or is too slow. E.g. when the data provided 
	 * by this source always is a moment in time then all realtime tests can be
	 * replaced by returning fixed boolean values.
	 */
	public getVersion = (datum: any) => datum.version;
	public getTime = (datum: any) => datum.time;
	public getStart = (datum: any) => datum.start;
	public getEnd = (datum: any) => datum.end;
	public getLat = (datum: any) => datum.lat;
	public getLng = (datum: any) => datum.lng;
	public getNorth = (datum: any) => datum.north;
	public getEast = (datum: any) => datum.east;
	public getSouth = (datum: any) => datum.south;
	public getWest = (datum: any) => datum.west;
	public compareByTime = (datum0: TemporalCoordinate, datum1: TemporalCoordinate) => util.compareByTime(datum0, datum1);
	public temporalHash = (datum: Datum) => util.datumTemporalHash(datum);
	public isTemporal = (datum: any) => util.isTemporal(datum);
	public isMoment = (datum: any) => util.isMoment(datum);
	public isRange = (datum: any) => util.isRange(datum);
	public isPerpetual = (datum: any) => util.isPerpetual(datum);
	public isSince = (datum: any) => util.isSince(datum);
	public isUntil = (datum: any) => util.isUntil(datum);
	public hasStart = (datum: any) => util.hasStart(datum);
	public hasEnd = (datum: any) => util.hasEnd(datum);
	public getDuration = (datum: any) => util.duration(datum);
	public compareByLocation = (datum0: SpatialCoordinate, datum1: SpatialCoordinate) => util.compareByLocation(datum0, datum1);
	public spatialHash = (datum: Datum) => util.datumSpatialHash(datum);
	public isSpatial = (datum: any) => util.isSpatial(datum);
	public isPoint = (datum: any) => util.isPoint(datum);
	public isArea = (datum: any) => util.isArea(datum);
	public isVersional = (datum: any) => util.isVersional(datum);

	protected handleData(timeRange: TimelineRange, animationTiming: CursorMillis[],types: string[], data: any): void {
		this.getMeta().version = this.storeData(timeRange, animationTiming, data);
		this.getMeta().timeRange = timeRange;
		this.getMeta().animationTiming = animationTiming;
		
		this.notifyNewMeta();

		this.updateSelections(VisualizationUpdateLevel.data, types);

	}

	protected isReady() {
		return this.timeline != null && this.map != null;
	}

	protected getDatumType( datum:any ): string {
		return <string>datum.type;
	}

	/**
   * Gets called when new (unprocessed) data has arrived.
   * The metaData will be updated after this method returns. This method
   * must return the highest data version number found in the dataset
   * managed by this data source. It will be stored in the metaData.
   * @return the highest (youngest) version number found in the dataset managed by this data source.
   */
	private storeData(timeRange: TimelineRange, animationTiming: CursorMillis[], data: any): number {
		//console.info(`data: %o`, data);
		data = data ? data : [];

		let dataMap: Map<string, any[]> = data.reduce((dataMap: Map<string, any[]>, datum: any) => {
			let datumType = this.getDatumType(datum);
			let typeData = dataMap.get(datumType);
			if (typeData === undefined) {
				typeData = [];
				dataMap.set(datumType, typeData);
			}
			typeData.push(datum);
			return dataMap;
		}, new Map<string, any[]>());

		let version: number = null;
		let now = Date.now();

		Object.entries(this.typeDataMap).forEach(entry => {
			let type = entry[0];
			let dataList = entry[1];
			let typeData = dataMap.get(type);
			dataList.mergeData(timeRange, animationTiming, now, typeData);
			let typeVersion = dataList.getMeta().version;

			version = version == null || (typeVersion != null && version < typeVersion) ? typeVersion : version;
		});

		return version;
	}

	private forEachDataList(func: (data: DataList) => void, types?: string | string[]): string[] {
		let called: string[];
		let filter: (entry: [string, DataList]) => boolean;

		if (types) {
			if ((<string>types).substring) {
				filter = entry => entry[0] === <string>types;
			} else {
				filter = entry => (<string[]>types).includes(entry[0]);
			}
		} else {
			filter = _entry => true;
		}

		called = Object.entries(this.typeDataMap)
			.filter(filter)
			.reduce((typeList, entry) => {
				func(entry[1]);
				typeList.push(entry[0]);
				return typeList;
			}, <string[]>[]);

		return called;
	}

	private forEachVisualizer(func: (visualizer: Visualizer) => void, types?: string | string[], filterOnKeys = false): string[] {
		let called: string[];
		let filter: (entry: [string, Visualizer]) => boolean;

		if (types) {
			if ((<string>types).substring) {
				filter = filterOnKeys === true
					? entry => entry[0].startsWith(<string>types)
					: entry => entry[1].type() == undefined || (<string>types) === entry[1].type();
			} else {
				filter = filterOnKeys === true
					? entry => entry[0].startsWith(<string>types)
					: entry => entry[1].type() == undefined || (<string[]>types).includes(entry[1].type());
			}
		} else {
			filter = _entry => true;
		}

		called = Object.entries(this.visualizers)
			.filter(filter)
			.reduce((typeList, entry) => {
				func(entry[1]);
				typeList.push(entry[0]);
				return typeList;
			}, <string[]>[]);

		return called;
	}

}
